// Tenant-scoped usePersistedState (spec: docs/specs/scout-spray-filter-hygiene.md).
//
// Persisted list filters used to share one localStorage key across tenants,
// so a date range or block filter set on tenant A silently pre-filtered
// tenant B's data — the scout report printed the wrong window and the
// Scouting module read as empty on entry (Thomas, 2026-07-22). Suffixing
// the key with the active tenant id keeps the persistence feature (filters
// survive a detail-page round trip) per tenant.
//
// Semantics:
//   - Tenant still resolving → plain state, no read/write. Never write
//     under a bogus key.
//   - Tenant RESOLVES (null→key): adopt the key's stored value — unless the
//     user already changed the value while it resolved (typed a search,
//     tapped Clear on a cold boot); their input wins and is persisted.
//   - Tenant SWITCHES (key→key): always adopt the new key's stored value,
//     DURING render (React's adjust-state-on-render pattern), so the write
//     effect below can never clobber the new tenant's stored value with
//     the old tenant's.
//   - Old un-scoped keys are simply abandoned (one-time filter reset per
//     tenant after deploy — harmless, arguably the point).
//
// Use for filters whose values are tenant-specific (block ids, searches,
// date windows over tenant data). Tenant-neutral preferences (active tab,
// sort direction, print toggles) stay on plain usePersistedState.
import { useEffect, useRef, useState } from 'react'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { readPersisted, writePersisted } from './usePersistedState'

export function useTenantPersistedState<T>(baseKey: string, initial: T): [T, (v: T | ((prev: T) => T)) => void] {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  const key = tenantId ? `${baseKey}.${tenantId}` : null

  const [value, setValue] = useState<T>(() => (key ? readPersisted(key, initial) : initial))
  const touchedPreResolve = useRef(false)

  const lastKey = useRef(key)
  if (lastKey.current !== key) {
    const resolving = lastKey.current === null && key !== null
    lastKey.current = key
    if (!(resolving && touchedPreResolve.current)) {
      setValue(key ? readPersisted(key, initial) : initial)
    }
    touchedPreResolve.current = false
  }

  useEffect(() => {
    if (!key) return
    writePersisted(key, value)
  }, [key, value])

  const set = (v: T | ((prev: T) => T)) => {
    if (!lastKey.current) touchedPreResolve.current = true
    setValue(v)
  }
  return [value, set]
}
